import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable/index";
import { toast } from "sonner";
import { z } from "zod";
import { useTranslation, Trans } from "react-i18next";
import { PublicNav } from "@/components/PublicNav";
import { PublicFooter } from "@/components/PublicFooter";
import { OCCUPATION_OPTIONS, isOccupation, type Occupation } from "@/lib/occupation";
import { COUNTRIES } from "@/lib/countries";

export const Route = createFileRoute("/register")({
  head: () => ({
    meta: [
      { title: "Create account — VISIKT" },
      {
        name: "description",
        content:
          "Create your VISIKT account to track subscriptions, salary growth, and real purchasing power.",
      },
      { property: "og:title", content: "Create account — VISIKT" },
      {
        property: "og:description",
        content:
          "Create your VISIKT account to track subscriptions, salary growth, and real purchasing power.",
      },
      { property: "og:url", content: "/register" },
      { name: "robots", content: "noindex" },
    ],
    links: [{ rel: "canonical", href: "/register" }],
  }),
  component: RegisterPage,
});

interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  postalCode: string;
  city: string;
  country: string;
  phone: string;
  occupation: Occupation | "";
  password: string;
  confirmPassword: string;
  agree: boolean;
}

const emailSchema = z.string().trim().email().max(255);
const passwordSchema = z.string().min(8).max(128);
const phoneRegex = /^[+\d][\d\s\-()]{6,}$/;

function RegisterPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<FormState>({
    firstName: "",
    lastName: "",
    email: "",
    postalCode: "",
    city: "",
    country: "Norway",
    phone: "",
    occupation: "",
    password: "",
    confirmPassword: "",
    agree: false,
  });
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) navigate({ to: "/plans", replace: true });
    });
  }, [navigate]);

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  }

  function validate(): boolean {
    const next: Partial<Record<keyof FormState, string>> = {};
    if (!form.firstName.trim()) next.firstName = t("register.errors.required");
    if (!form.lastName.trim()) next.lastName = t("register.errors.required");
    if (!emailSchema.safeParse(form.email).success) next.email = t("auth.invalidEmail");
    if (!form.city.trim()) next.city = t("register.errors.required");
    if (!form.country.trim()) next.country = t("register.errors.required");
    if (form.country === "Norway") {
      if (!/^\d{4}$/.test(form.postalCode.trim())) next.postalCode = t("register.errors.postalNo");
    } else if (!form.postalCode.trim()) {
      next.postalCode = t("register.errors.required");
    }
    if (!phoneRegex.test(form.phone.trim())) next.phone = t("register.errors.phone");
    if (!isOccupation(form.occupation)) next.occupation = t("register.errors.required");
    if (!passwordSchema.safeParse(form.password).success) next.password = t("auth.passwordMin");
    if (form.password !== form.confirmPassword)
      next.confirmPassword = t("register.errors.confirmMismatch");
    if (!form.agree) next.agree = t("register.errors.agreeRequired");
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const displayName = `${form.firstName.trim()} ${form.lastName.trim()}`.trim();
      const { error, data } = await supabase.auth.signUp({
        email: form.email.trim(),
        password: form.password,
        options: {
          emailRedirectTo: `${window.location.origin}/dashboard`,
          data: {
            display_name: displayName,
            first_name: form.firstName.trim(),
            last_name: form.lastName.trim(),
            postal_code: form.postalCode.trim(),
            city: form.city.trim(),
            country: form.country,
            phone: form.phone.trim(),
            occupation: form.occupation,
          },
        },
      });
      if (error) throw error;
      if (data.session) {
        navigate({ to: "/plans", replace: true });
      } else {
        toast.success(t("register.checkEmail"));
        navigate({ to: "/auth", replace: true });
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : t("common.somethingWrong");
      const already = /already|registered|exists/i.test(msg);
      if (already) {
        toast.error(t("register.errors.alreadyRegistered"));
        setErrors({ email: t("register.errors.alreadyRegistered") });
      } else {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogle() {
    setLoading(true);
    const result = await lovable.auth.signInWithOAuth("google", {
      redirect_uri: window.location.origin,
    });
    if (result.error) {
      setLoading(false);
      toast.error(t("auth.googleFailed"));
      return;
    }
    if (result.redirected) return;
    navigate({ to: "/complete-profile", replace: true });
  }

  async function handleApple() {
    setLoading(true);
    const result = await lovable.auth.signInWithOAuth("apple", {
      redirect_uri: window.location.origin,
    });
    if (result.error) {
      setLoading(false);
      toast.error(t("auth.appleFailed"));
      return;
    }
    if (result.redirected) return;
    navigate({ to: "/complete-profile", replace: true });
  }

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <header className="px-5 pt-8 pb-8 max-w-md mx-auto w-full flex items-center justify-between gap-3">
        <Link to="/" className="font-display font-extrabold tracking-tighter text-xl">
          VISIKT
        </Link>
        <PublicNav />
      </header>

      <main className="flex-1 px-5 max-w-md mx-auto w-full">
        <div className="mb-8 animate-entry">
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
            {t("register.eyebrow")}
          </p>
          <h1 className="text-3xl font-display font-bold tracking-tight">
            {t("register.headline")}
          </h1>
          <p className="mt-3 text-sm text-muted-foreground">{t("register.subtitle")}</p>
        </div>

        <button
          type="button"
          onClick={handleGoogle}
          disabled={loading}
          className="w-full mb-2 py-3 rounded-md bg-foreground text-background font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-50 transition-opacity"
        >
          <GoogleIcon /> {t("auth.continueGoogle")}
        </button>
        <button
          type="button"
          onClick={handleApple}
          disabled={loading}
          className="w-full mb-3 py-3 rounded-md bg-foreground text-background font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-50 transition-opacity"
        >
          <AppleIcon /> {t("auth.continueApple")}
        </button>
        <div className="flex items-center gap-3 my-5">
          <div className="flex-1 h-px bg-border" />
          <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
            {t("auth.or")}
          </span>
          <div className="flex-1 h-px bg-border" />
        </div>

        <form onSubmit={handleSubmit} className="space-y-3" noValidate>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("register.firstName")} error={errors.firstName}>
              <input
                type="text"
                required
                value={form.firstName}
                onChange={(e) => update("firstName", e.target.value)}
                maxLength={80}
                autoComplete="given-name"
                className="input-base"
              />
            </Field>
            <Field label={t("register.lastName")} error={errors.lastName}>
              <input
                type="text"
                required
                value={form.lastName}
                onChange={(e) => update("lastName", e.target.value)}
                maxLength={80}
                autoComplete="family-name"
                className="input-base"
              />
            </Field>
          </div>

          <Field label={t("auth.email")} error={errors.email}>
            <input
              type="email"
              required
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              maxLength={255}
              autoComplete="email"
              placeholder="name@example.com"
              className="input-base"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label={t("register.postalCode")} error={errors.postalCode}>
              <input
                type="text"
                required
                value={form.postalCode}
                onChange={(e) => update("postalCode", e.target.value)}
                maxLength={16}
                autoComplete="postal-code"
                placeholder={form.country === "Norway" ? "0150" : ""}
                className="input-base"
              />
            </Field>
            <Field label={t("register.city")} error={errors.city}>
              <input
                type="text"
                required
                value={form.city}
                onChange={(e) => update("city", e.target.value)}
                maxLength={80}
                autoComplete="address-level2"
                className="input-base"
              />
            </Field>
          </div>

          <Field label={t("register.country")} error={errors.country}>
            <select
              value={form.country}
              onChange={(e) => update("country", e.target.value)}
              className="input-base"
              autoComplete="country-name"
            >
              {COUNTRIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </Field>

          <Field label={t("register.phone")} error={errors.phone}>
            <input
              type="tel"
              required
              value={form.phone}
              onChange={(e) => update("phone", e.target.value)}
              maxLength={32}
              autoComplete="tel"
              placeholder="+47 900 00 000"
              className="input-base"
            />
          </Field>

          <Field label={t("occupation.label")} error={errors.occupation}>
            <select
              required
              value={form.occupation}
              onChange={(e) => update("occupation", e.target.value as Occupation | "")}
              className="input-base w-full"
            >
              <option value="" disabled>
                {t("occupation.placeholder")}
              </option>
              {OCCUPATION_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {t(o.labelKey)}
                </option>
              ))}
            </select>
          </Field>

          <Field label={t("auth.password")} error={errors.password}>
            <input
              type="password"
              required
              value={form.password}
              onChange={(e) => update("password", e.target.value)}
              minLength={8}
              maxLength={128}
              autoComplete="new-password"
              placeholder={t("auth.passwordHint")}
              className="input-base"
            />
          </Field>

          <Field label={t("register.confirmPassword")} error={errors.confirmPassword}>
            <input
              type="password"
              required
              value={form.confirmPassword}
              onChange={(e) => update("confirmPassword", e.target.value)}
              minLength={8}
              maxLength={128}
              autoComplete="new-password"
              className="input-base"
            />
          </Field>

          <label className="flex items-start gap-2.5 text-xs text-muted-foreground mt-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.agree}
              onChange={(e) => update("agree", e.target.checked)}
              className="mt-0.5 size-4 accent-accent flex-shrink-0"
              aria-invalid={!!errors.agree}
            />
            <span className="leading-relaxed">
              <Trans
                i18nKey="register.agreeTos"
                components={{
                  tosLink: (
                    <a
                      href="/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline underline-offset-4 text-foreground"
                    />
                  ),
                  privLink: (
                    <a
                      href="/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline underline-offset-4 text-foreground"
                    />
                  ),
                }}
              />
            </span>
          </label>
          {errors.agree && (
            <span className="block text-[11px] text-destructive">{errors.agree}</span>
          )}

          <button
            type="submit"
            disabled={loading || !form.agree}
            className="w-full py-3 rounded-md bg-accent text-primary font-medium text-sm disabled:opacity-50 transition-opacity mt-2"
          >
            {loading ? t("auth.waiting") : t("register.submit")}
          </button>
        </form>

        <div className="mt-6 flex flex-col items-center gap-2 text-xs text-muted-foreground">
          <p>
            {t("auth.haveAccount")}{" "}
            <Link to="/auth" className="text-foreground underline underline-offset-4">
              {t("auth.submitSignIn")}
            </Link>
          </p>
        </div>
      </main>
      <div className="px-5 max-w-md mx-auto w-full mt-10">
        <PublicFooter />
      </div>
    </div>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block font-mono text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5">
        {label}
      </span>
      {children}
      {error && <span className="block text-[11px] text-destructive mt-1">{error}</span>}
    </label>
  );
}

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
      <path
        fill="#fff"
        d="M21.35 11.1H12v2.9h5.35c-.23 1.46-1.66 4.3-5.35 4.3a6.3 6.3 0 1 1 0-12.6c1.78 0 2.97.76 3.65 1.41l2.49-2.4C16.5 3.2 14.45 2.3 12 2.3 6.65 2.3 2.35 6.6 2.35 11.95S6.65 21.6 12 21.6c6.93 0 9.6-4.86 9.6-9.6 0-.65-.07-1.14-.25-1.9Z"
      />
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
      <path
        fill="#fff"
        d="M16.365 1.43c0 1.14-.42 2.23-1.19 3.05-.79.86-2.05 1.52-3.11 1.43-.13-1.09.42-2.23 1.17-3.02.83-.88 2.24-1.53 3.13-1.46Zm4.02 17.03c-.55 1.27-.82 1.83-1.53 2.95-.99 1.55-2.39 3.48-4.12 3.49-1.55.02-1.95-1.01-4.05-1-2.1.01-2.54 1.02-4.09 1-1.73-.02-3.06-1.76-4.05-3.31C-.68 18.06-.98 12.9 1.87 10.13c1.02-.99 2.41-1.62 3.87-1.65 1.6-.03 3.11 1.08 4.09 1.08.98 0 2.82-1.33 4.75-1.14.81.03 3.08.33 4.54 2.46-3.9 2.14-3.28 7.71 1.27 9.58Z"
      />
    </svg>
  );
}
